/**
 * Import Hooks - TanStack Query hooks pour le module Import
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { importService } from '../services/import.service';
import type {
  ImportType,
  ImportConfig,
  ImportStatus,
  ImportLinesParams,
  UpdateImportLineRequest,
} from '../types/import.types';

// Query keys
export const importKeys = {
  all: ['imports'] as const,
  types: () => [...importKeys.all, 'types'] as const,
  history: (params?: { type?: ImportType; status?: ImportStatus; page?: number; limit?: number }) =>
    [...importKeys.all, 'history', params] as const,
  detail: (id: string) => [...importKeys.all, 'detail', id] as const,
  preview: (id: string) => [...importKeys.all, 'preview', id] as const,
  status: (id: string) => [...importKeys.all, 'status', id] as const,
  // Keys pour les lignes d'import (Enterprise UX)
  lines: (importId: string, params?: ImportLinesParams) =>
    [...importKeys.all, 'lines', importId, params] as const,
};

// ==================== TYPES ====================

export function useImportTypes() {
  return useQuery({
    queryKey: importKeys.types(),
    queryFn: () => importService.getTypes(),
    staleTime: 24 * 60 * 60 * 1000, // 24 hours - types don't change often
  });
}

// ==================== HISTORY ====================

export function useImportHistory(params?: {
  type?: ImportType;
  status?: ImportStatus;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: importKeys.history(params),
    queryFn: () => importService.getHistory(params),
  });
}

// ==================== IMPORT DETAIL ====================

export function useImportStatus(importId: string | null, options?: { refetchInterval?: number }) {
  return useQuery({
    queryKey: importKeys.status(importId || ''),
    queryFn: () => importService.getStatus(importId!),
    enabled: !!importId,
    refetchInterval: options?.refetchInterval,
  });
}

export function useImportPreview(importId: string | null) {
  return useQuery({
    queryKey: importKeys.preview(importId || ''),
    queryFn: () => importService.getPreview(importId!),
    enabled: !!importId,
  });
}

// ==================== MUTATIONS ====================

export function useUploadImport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      file,
      type,
      config,
    }: {
      file: File;
      type: ImportType;
      config?: ImportConfig;
    }) => importService.upload(file, type, config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: importKeys.history() });
    },
  });
}

export function useUpdateImportConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ importId, config }: { importId: string; config: ImportConfig }) =>
      importService.updateConfig(importId, config),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: importKeys.preview(variables.importId) });
    },
  });
}

export function useValidateImport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (importId: string) => importService.validate(importId),
    onSuccess: (_, importId) => {
      queryClient.invalidateQueries({ queryKey: importKeys.status(importId) });
    },
  });
}

export function useProcessImport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (importId: string) => importService.process(importId),
    onSuccess: (_, importId) => {
      queryClient.invalidateQueries({ queryKey: importKeys.status(importId) });
      queryClient.invalidateQueries({ queryKey: importKeys.history() });
    },
  });
}

export function useCancelImport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (importId: string) => importService.cancel(importId),
    onSuccess: (_, importId) => {
      queryClient.invalidateQueries({ queryKey: importKeys.status(importId) });
      queryClient.invalidateQueries({ queryKey: importKeys.history() });
    },
  });
}

export function useRollbackImport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (importId: string) => importService.rollback(importId),
    onSuccess: (_, importId) => {
      queryClient.invalidateQueries({ queryKey: importKeys.status(importId) });
      queryClient.invalidateQueries({ queryKey: importKeys.history() });
    },
  });
}

// ============================================================================
// HOOKS POUR LA GESTION PAGINÉE DES LIGNES D'IMPORT (Enterprise UX)
// ============================================================================

/**
 * Hook pour récupérer les lignes d'un import avec pagination, filtres et tri
 * Utilise TanStack Query pour la gestion du cache et des états
 */
export function useImportLines(importId: string | null, params?: ImportLinesParams) {
  return useQuery({
    queryKey: importKeys.lines(importId || '', params),
    queryFn: () => importService.getImportLines(importId!, params),
    enabled: !!importId,
    // Garder les données précédentes pendant le chargement (pour éviter le flash)
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Hook pour mettre à jour une ligne d'import (correction inline)
 */
export function useUpdateImportLine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      importId,
      lineId,
      data,
    }: {
      importId: string;
      lineId: string;
      data: UpdateImportLineRequest;
    }) => importService.updateImportLine(importId, lineId, data),
    onSuccess: (_, variables) => {
      // Invalider les lignes pour rafraîchir les données
      queryClient.invalidateQueries({
        queryKey: importKeys.lines(variables.importId),
        exact: false,
      });
      // Invalider aussi le statut de l'import (pour les compteurs)
      queryClient.invalidateQueries({
        queryKey: importKeys.status(variables.importId),
      });
    },
  });
}

/**
 * Hook pour exporter les erreurs d'un import au format CSV
 */
export function useExportImportErrors() {
  return useMutation({
    mutationFn: (importId: string) => importService.exportImportErrors(importId),
  });
}

/**
 * Hook pour revalider toutes les lignes d'un import
 */
export function useRevalidateImport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (importId: string) => importService.revalidateImport(importId),
    onSuccess: (_, importId) => {
      // Invalider les lignes et le statut
      queryClient.invalidateQueries({
        queryKey: importKeys.lines(importId),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: importKeys.status(importId),
      });
      queryClient.invalidateQueries({
        queryKey: importKeys.preview(importId),
      });
    },
  });
}

/**
 * Hook pour réinitialiser un import bloqué (PROCESSING ou FAILED)
 * Permet de relancer le traitement après un timeout ou crash
 */
export function useResetStuckImport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      importId,
      targetStatus,
    }: {
      importId: string;
      targetStatus?: 'VALIDATED' | 'PARSED';
    }) => importService.resetStuckImport(importId, targetStatus),
    onSuccess: (_, variables) => {
      // Invalider toutes les queries liées à cet import
      queryClient.invalidateQueries({
        queryKey: importKeys.lines(variables.importId),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: importKeys.status(variables.importId),
      });
      queryClient.invalidateQueries({
        queryKey: importKeys.preview(variables.importId),
      });
      queryClient.invalidateQueries({
        queryKey: importKeys.history(),
      });
    },
  });
}
